import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { INITIAL_VALUES } from 'modules/RACAffordabilityEngine/RACAffordabilityEngine.const';
import {
  IAvailableFilters,
  ILenderVehicle,
  IRACVehicle,
  IRACVehicleByStock,
  IRACVehiclesResponse,
  ITier,
  IVehicleFiltersDirtyFields,
} from 'modules/core/types';
import { IIncomeForm } from 'components/IncomeCalculator/types';
import { IRACAffordabilityEngineForm } from 'modules/RACAffordabilityEngine/types/RACAffordabilityEngineForm';
import {
  getLoanVariablesInfoThunk,
  getRACVehiclesThunk,
  getRACVehiclesByStockThunk,
  getLoanTermsListThunk,
  fetchDealSourceDataThunk,
  getRateSheetsDataThunk,
  saveRateSheetsDataThunk,
  submitDealSourceDataThunk,
  getLenderLoanVariablesInfoThunk,
  getLenderLoanTermsListThunk,
} from 'actions/vehicleActions';
import { ILoanVariables, ILoanVariablesForm } from 'components/Modals/LoanVariablesDialog/types';
import { getRegionControlsThunk, getTierOptionsThunk } from 'actions/controlActions';
import { ISalesFeesForm } from 'components/Modals/LoanVariablesDialog/modals/Calculators/SaleFeesCalculator/types';
import { INetTradeInForm } from 'components/Modals/LoanVariablesDialog/modals/Calculators/NetTradeInCalculator/types';
import { IDealSourceData, IDealSourceDataFlags } from 'modules/core/types/dealSource';
import { IRegionControls, IRegionControlsPayload } from 'modules/core/types/region';
import { IRateSheetData } from 'modules/core/types/rateSheets';
import {
  DEAL_SOURCE_DATA_FLAGS_INITIAL,
  computeGetFulfilledFlags,
  computeGetPendingFlags,
  computeGetRejectedFlags,
  computePostFulfilledFlags,
  computePostPendingFlags,
  computePostRejectedFlags,
} from 'store/utils/dealSourceDataFlags.utils';
import { preserveManuallyEnteredVehicleInfo } from 'store/utils/dealSourceData.utils';

export interface IRACAffordabilityEngineState {
  initialValues: IRACAffordabilityEngineForm;
  initialErrors: Record<string, string>;
  vehicles: {
    vehiclesByParams?: IRACVehicle[];
    vehiclesByStock?: IRACVehicle[];
    sortedVehicles?: IRACVehicle[];
  };
  income: IIncomeForm;
  isFetchingVehicles: boolean;
  isFetchingVehiclesFailed: boolean;
  fetchVehiclesError?: any;
  resetCount: number;
  loanVariablesInfo: ILoanVariables;
  loanVariablesCurrentVehicle?: IRACVehicle | ILenderVehicle;
  dealSourceData: IDealSourceData;
  ratesheetsData: IRateSheetData;
  tiers: ITier[];
  regionControls: IRegionControls;
  isTableView: boolean;
  notFoundVehicles: string[];
  notAvailableVehicles: string[];
  vehicleFiltersDirtyFields: IVehicleFiltersDirtyFields;
  availableFilters?: IAvailableFilters;
  firstDealSourceRender: boolean;
  isFetchingLoanVariablesInfo: boolean;
  rateSheetsDataFlags: IRateSheetDataFlags;
  dealSourceDataFlags: IDealSourceDataFlags;
  dealSourceTierEditable: boolean;
}

interface IRateSheetDataFlags {
  isSavingRateSheets: boolean;
  isFetchingRateSheets: boolean;
  isShow?: boolean;
  isCalculatedPayment?: boolean;
}

const rateSheetsDataFlagsInitialValues = {
  isSavingRateSheets: false,
  isFetchingRateSheets: true,
  isShow: false,
  isCalculatedPayment: false,
};

const initialState: IRACAffordabilityEngineState = {
  initialValues: INITIAL_VALUES,
  initialErrors: {},
  vehicles: {
    vehiclesByParams: undefined,
    vehiclesByStock: undefined,
    sortedVehicles: undefined,
  },
  income: INITIAL_VALUES.income,
  isFetchingVehicles: false,
  isFetchingVehiclesFailed: false,
  resetCount: 0,
  loanVariablesInfo: {} as ILoanVariables,
  dealSourceData: {} as IDealSourceData,
  ratesheetsData: {},
  tiers: [],
  regionControls: {} as IRegionControls,
  isTableView: true,
  notFoundVehicles: [],
  notAvailableVehicles: [],
  vehicleFiltersDirtyFields: {},
  availableFilters: undefined,
  firstDealSourceRender: true,
  rateSheetsDataFlags: rateSheetsDataFlagsInitialValues,
  isFetchingLoanVariablesInfo: false,
  dealSourceDataFlags: DEAL_SOURCE_DATA_FLAGS_INITIAL,
  dealSourceTierEditable: false,
};

export const RACAffordabilityEngineSlice = createSlice({
  name: 'RACAffordabilityEngine',
  initialState,
  reducers: {
    syncInitialValues: (
      state: IRACAffordabilityEngineState,
      { payload: initialValues }: PayloadAction<IRACAffordabilityEngineForm>,
    ) => ({
      ...state,
      initialValues,
    }),
    syncInitialErrors: (
      state: IRACAffordabilityEngineState,
      { payload: initialErrors }: PayloadAction<Record<string, string>>,
    ) => ({
      ...state,
      initialErrors,
    }),
    closeRateSheetsModal: (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: { isSavingRateSheets: false, isShow: false, isFetchingRateSheets: false },
    }),
    clearVehicleDetails: (state: IRACAffordabilityEngineState) => ({
      ...state,
      loanVariablesInfo: initialState.loanVariablesInfo,
    }),
    setIncome: (state: IRACAffordabilityEngineState, { payload: income }: PayloadAction<IIncomeForm>) => ({
      ...state,
      income,
    }),
    setLoanVariablesCurrentVehicle: (
      state: IRACAffordabilityEngineState,
      { payload: vehicle }: PayloadAction<IRACVehicle | ILenderVehicle>,
    ) => ({
      ...state,
      loanVariablesCurrentVehicle: vehicle,
    }),
    clearLoanVariablesCurrentVehicle: (state: IRACAffordabilityEngineState) => ({
      ...state,
      loanVariablesCurrentVehicle: initialState.loanVariablesCurrentVehicle,
    }),
    setDealSourceData: (state: IRACAffordabilityEngineState, { payload }: PayloadAction<any>) => ({
      ...state,
      dealSourceData: payload,
      dealSourceDataFlags: {
        ...state.dealSourceDataFlags,
        isUpdated: true,
      },
    }),
    resetDealSourceDataFlags: (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: initialState.dealSourceDataFlags,
    }),
    resetDealSourceData: (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceData: initialState.dealSourceData,
    }),
    resetRACDealSourceRelatedData: (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceData: initialState.dealSourceData,
      dealSourceDataFlags: initialState.dealSourceDataFlags,
      vehicles: {
        vehiclesByParams: undefined,
        vehiclesByStock: undefined,
      },
    }),
    resetIncome: (state: IRACAffordabilityEngineState) => ({
      ...state,
      income: initialState.income,
    }),
    setFirstDealSourceRender: (state: IRACAffordabilityEngineState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      firstDealSourceRender: payload,
    }),
    setSalesFees: (state: IRACAffordabilityEngineState, { payload: salesFeesData }: PayloadAction<ISalesFeesForm>) => ({
      ...state,
      salesFeesData,
    }),
    setNetTradeIn: (
      state: IRACAffordabilityEngineState,
      { payload: netTradeInData }: PayloadAction<INetTradeInForm>,
    ) => ({
      ...state,
      netTradeInData,
    }),
    setLoanVariables: (
      state: IRACAffordabilityEngineState,
      { payload: LoanVariablesData }: PayloadAction<ILoanVariablesForm>,
    ) => ({
      ...state,
      LoanVariablesData,
    }),
    resetVehicles: (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicles: {
        vehiclesByParams: undefined,
        vehiclesByStock: undefined,
        sortedVehicles: undefined,
      },
      availableFilters: undefined,
    }),
    resetVehiclesByStock: (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByStock: undefined, sortedVehicles: undefined },
      availableFilters: state.availableFilters,
    }),
    resetVehiclesByParams: (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByParams: undefined, sortedVehicles: undefined },
      availableFilters: state.availableFilters,
    }),
    resetByStockOptions: (state: IRACAffordabilityEngineState) => ({
      ...state,
      notFoundVehicles: [],
      notAvailableVehicles: [],
    }),
    resetByStock: (state: IRACAffordabilityEngineState) => ({
      ...state,
      notFoundVehicles: [],
      notAvailableVehicles: [],
      vehicles: { ...state.vehicles, vehiclesByStock: undefined, sortedVehicles: undefined },
      availableFilters: state.availableFilters,
    }),
    incrementResetCount: (state: IRACAffordabilityEngineState) => ({
      ...state,
      resetCount: state.resetCount + 1,
    }),
    resetResetCount: (state: IRACAffordabilityEngineState) => ({
      ...state,
      resetCount: 0,
    }),
    setIsTableView: (state: IRACAffordabilityEngineState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isTableView: payload,
    }),
    resetVehicleFiltersDirtyFields: (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicleFiltersDirtyFields: {},
    }),
    setDealSourceTierEditable: (state: IRACAffordabilityEngineState, { payload }: PayloadAction<boolean>) => ({
      ...state,
      dealSourceTierEditable: payload,
    }),
    setSortedVehiclesData: (state: IRACAffordabilityEngineState, { payload }: PayloadAction<IRACVehicle[]>) => ({
      ...state,
      vehicles: {
        ...state.vehicles,
        sortedVehicles: payload,
      },
    }),
  },
  extraReducers: (builder) => {
    builder.addCase(
      getRACVehiclesThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload }: PayloadAction<IRACVehiclesResponse>) => {
        const vehicles = payload.results || [];
        const availableFilters = payload.available_filters;

        return {
          ...state,
          vehicles: {
            ...state.vehicles,
            vehiclesByParams: vehicles.map((vehicle) => ({ ...vehicle, is_new: false })),
          },
          availableFilters,
          isFetchingVehicles: false,
          isFetchingVehiclesFailed: false,
        };
      },
    );
    builder.addCase(getRACVehiclesThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByParams: undefined, sortedVehicles: undefined },
      availableFilters: state.availableFilters,
      resetCount: 0,
      isFetchingVehicles: true,
      isFetchingVehiclesFailed: false,
      fetchVehiclesError: undefined,
    }));
    builder.addCase(getRACVehiclesThunk.rejected, (state: IRACAffordabilityEngineState, action) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByParams: undefined },
      availableFilters: state.availableFilters,
      isFetchingVehicles: false,
      isFetchingVehiclesFailed: true,
      fetchVehiclesError: action.payload,
    }));
    builder.addCase(
      getRateSheetsDataThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: ratesheetsData }: any) => ({
        ...state,
        ratesheetsData,
        rateSheetsDataFlags: {
          isSavingRateSheets: false,
          isFetchingRateSheets: false,
          isShow: true,
        },
      }),
    );
    builder.addCase(getRateSheetsDataThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: {
        isSavingRateSheets: false,
        isFetchingRateSheets: true,
        isShow: false,
      },
    }));
    builder.addCase(getRateSheetsDataThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: {
        isSavingRateSheets: false,
        isFetchingRateSheets: true,
        isShow: false,
      },
    }));
    builder.addCase(saveRateSheetsDataThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: {
        isSavingRateSheets: true,
        isFetchingRateSheets: false,
        isShow: true,
      },
    }));
    builder.addCase(saveRateSheetsDataThunk.fulfilled, (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: {
        isSavingRateSheets: false,
        isFetchingRateSheets: false,
        isCalculatedPayment: true,
      },
    }));
    builder.addCase(saveRateSheetsDataThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      rateSheetsDataFlags: {
        isSavingRateSheets: false,
        isFetchingRateSheets: false,
        isShow: true,
      },
    }));
    builder.addCase(
      getLoanTermsListThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: loanTermsList }: any) => ({
        ...state,
        loanTermsList,
      }),
    );
    builder.addCase(getLoanTermsListThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
    }));
    builder.addCase(getLoanTermsListThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
    }));
    builder.addCase(
      getLenderLoanTermsListThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: loanTermsList }: any) => ({
        ...state,
        loanTermsList,
      }),
    );
    builder.addCase(getLenderLoanTermsListThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
    }));
    builder.addCase(getLenderLoanTermsListThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
    }));
    builder.addCase(
      getLoanVariablesInfoThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: loanVariablesInfo }: PayloadAction<ILoanVariables>) => ({
        ...state,
        loanVariablesInfo,
        isFetchingLoanVariablesInfo: false,
      }),
    );
    builder.addCase(getLoanVariablesInfoThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      isFetchingLoanVariablesInfo: true,
    }));
    builder.addCase(getLoanVariablesInfoThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      isFetchingLoanVariablesInfo: true,
    }));
    builder.addCase(
      getLenderLoanVariablesInfoThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: loanVariablesInfo }: PayloadAction<ILoanVariables>) => ({
        ...state,
        loanVariablesInfo,
        isFetchingLoanVariablesInfo: false,
      }),
    );
    builder.addCase(getLenderLoanVariablesInfoThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      isFetchingLoanVariablesInfo: true,
    }));
    builder.addCase(getLenderLoanVariablesInfoThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      isFetchingLoanVariablesInfo: true,
    }));
    builder.addCase(
      getTierOptionsThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: tiers }: PayloadAction<ITier[]>) => ({
        ...state,
        tiers,
      }),
    );
    builder.addCase(
      getRegionControlsThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload }: PayloadAction<IRegionControlsPayload>) => ({
        ...state,
        regionControls: {
          vscLimitAbove: payload.vsc_limit_above,
          vscLimitBelow: payload.vsc_limit_below,
          vscLimitVehicleValue: payload.vsc_limit_vehicle_value,
          gapLimit: payload.gap_limit,
          gapMinLtv: payload.gap_min_ltv,
        },
      }),
    );

    builder.addCase(
      fetchDealSourceDataThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload: dealSourceData }: PayloadAction<IDealSourceData>) => ({
        ...state,
        dealSourceData: preserveManuallyEnteredVehicleInfo(state.dealSourceData, dealSourceData),
        dealSourceDataFlags: computeGetFulfilledFlags(state.dealSourceDataFlags),
      }),
    );
    builder.addCase(fetchDealSourceDataThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: computeGetPendingFlags(state.dealSourceDataFlags),
    }));
    builder.addCase(fetchDealSourceDataThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: computeGetRejectedFlags(state.dealSourceDataFlags),
    }));

    builder.addCase(submitDealSourceDataThunk.fulfilled, (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: computePostFulfilledFlags(state.dealSourceDataFlags),
    }));
    builder.addCase(submitDealSourceDataThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: computePostPendingFlags(state.dealSourceDataFlags),
    }));
    builder.addCase(submitDealSourceDataThunk.rejected, (state: IRACAffordabilityEngineState) => ({
      ...state,
      dealSourceDataFlags: computePostRejectedFlags(state.dealSourceDataFlags),
    }));
    builder.addCase(
      getRACVehiclesByStockThunk.fulfilled,
      (state: IRACAffordabilityEngineState, { payload }: PayloadAction<IRACVehicleByStock>) => ({
        ...state,
        vehicles: { ...state.vehicles, vehiclesByStock: payload.vehicles },
        notFoundVehicles: payload.search_result.not_in_db,
        notAvailableVehicles: payload.search_result.not_available,
        availableFilters: state.availableFilters,
        isFetchingVehicles: false,
        isFetchingVehiclesFailed: false,
      }),
    );

    builder.addCase(getRACVehiclesByStockThunk.pending, (state: IRACAffordabilityEngineState) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByStock: undefined, sortedVehicles: undefined },
      notFoundVehicles: [],
      notAvailableVehicles: [],
      availableFilters: state.availableFilters,
      resetCount: 0,
      isFetchingVehicles: true,
      isFetchingVehiclesFailed: false,
      fetchVehiclesError: undefined,
    }));

    builder.addCase(getRACVehiclesByStockThunk.rejected, (state: IRACAffordabilityEngineState, action) => ({
      ...state,
      vehicles: { ...state.vehicles, vehiclesByStock: undefined },
      notFoundVehicles: [],
      notAvailableVehicles: [],
      availableFilters: state.availableFilters,
      isFetchingVehicles: false,
      isFetchingVehiclesFailed: true,
      fetchVehiclesError: action.payload,
    }));
  },
});

export const {
  clearVehicleDetails,
  setIncome,
  setSalesFees,
  setNetTradeIn,
  setLoanVariables,
  setLoanVariablesCurrentVehicle,
  clearLoanVariablesCurrentVehicle,
  resetVehicles,
  resetVehiclesByStock,
  resetVehiclesByParams,
  resetIncome,
  resetByStockOptions,
  resetByStock,
  incrementResetCount,
  resetResetCount,
  setIsTableView,
  setFirstDealSourceRender,
  resetVehicleFiltersDirtyFields,
  closeRateSheetsModal,
  resetDealSourceDataFlags,
  resetDealSourceData,
  resetRACDealSourceRelatedData,
  setDealSourceTierEditable,
  setSortedVehiclesData,
  setDealSourceData,
} = RACAffordabilityEngineSlice.actions;
